import { Injectable } from '@angular/core';
import type { TransactionColumn } from '../models';

/** localStorage key for persisting the user's column configuration. */
const COLUMN_PREFS_KEY = 'transaction-table-columns-pref';

/** Shape of each entry stored in localStorage. */
export interface ColumnPref {
  key: string;
  visible: boolean;
  /** Persisted manual resize width in pixels. Absent → use the default width. */
  width?: number;
}

/** Resolved column configuration: ordered columns plus their visibility + width maps. */
export interface ResolvedColumns {
  columns: TransactionColumn[];
  visibility: Map<string, boolean>;
  widths: Map<string, number>;
}

/**
 * ColumnPreferencesService
 *
 * Owns the `localStorage` boundary for the transaction table's column
 * configuration (order, visibility, widths). Extracted from `TransactionStore` so the
 * persistence concern is isolated behind a single, mockable API and can be
 * unit-tested with a fake storage.
 *
 * Responsibilities:
 *  - {@link read}    — parse + validate stored prefs
 *  - {@link save}    — serialise the current configuration
 *  - {@link resolve} — pure merge of API columns with stored prefs (no I/O)
 */
@Injectable({ providedIn: 'root' })
export class ColumnPreferencesService {
  /**
   * Reads the stored column preferences from localStorage.
   * @returns Parsed array of `{ key, visible }` entries, or `null` if absent / invalid.
   */
  read(): ColumnPref[] | null {
    try {
      const raw = globalThis.localStorage?.getItem(COLUMN_PREFS_KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      if (
        !Array.isArray(parsed) ||
        parsed.length === 0 ||
        !parsed.every((p): p is ColumnPref => {
          if (typeof p !== 'object' || p === null) return false;
          const rec = p as Record<string, unknown>;
          const widthValid = rec['width'] === undefined || typeof rec['width'] === 'number';
          return (
            typeof rec['key'] === 'string' && typeof rec['visible'] === 'boolean' && widthValid
          );
        })
      ) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * Serialises the given column configuration (order + visibility) and writes
   * it to localStorage. Fails silently when storage is unavailable.
   *
   * @param columns    - Columns in their current display order
   * @param visibility - Map of column key → visible flag
   * @param widths     - Map of column key → manual resize width (px)
   */
  save(
    columns: TransactionColumn[],
    visibility: Map<string, boolean>,
    widths: Map<string, number>,
  ): void {
    try {
      const prefs: ColumnPref[] = columns.map((col) => {
        const width = widths.get(col.key);
        return {
          key: col.key,
          visible: visibility.get(col.key) ?? false,
          ...(width !== undefined ? { width } : {}),
        };
      });
      globalThis.localStorage?.setItem(COLUMN_PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // localStorage may be unavailable (private browsing quota, etc.) — fail silently
    }
  }

  /**
   * Merges API columns with stored prefs (or falls back to API defaults) into a
   * resolved `{ columns, visibility }` pair. Pure — performs no I/O beyond the
   * single {@link read} call, and never writes.
   *
   * When prefs exist: prefs control order + visibility; API columns absent from
   * prefs are appended at the end using their `default` flag. Guarantees at
   * least one visible column.
   *
   * @param apiColumns - Columns as returned by the API
   * @returns Ordered columns and their initial visibility map
   */
  resolve(apiColumns: TransactionColumn[]): ResolvedColumns {
    const prefs = this.read();
    if (!prefs) {
      // No stored prefs — use API defaults
      const visibility = new Map<string, boolean>();
      apiColumns.forEach((col) => visibility.set(col.key, col.default));
      return { columns: apiColumns, visibility, widths: new Map() };
    }

    // Merge: prefs control order + visibility + widths; new API columns append at end.
    const apiMap = new Map(apiColumns.map((c) => [c.key, c]));
    const ordered: TransactionColumn[] = [];
    const visibility = new Map<string, boolean>();
    const widths = new Map<string, number>();

    for (const pref of prefs) {
      const col = apiMap.get(pref.key);
      if (col) {
        ordered.push(col);
        visibility.set(col.key, pref.visible);
        if (pref.width !== undefined) widths.set(col.key, pref.width);
      }
    }
    // Append API columns absent from stored prefs
    for (const col of apiColumns) {
      if (!prefs.some((p) => p.key === col.key)) {
        ordered.push(col);
        visibility.set(col.key, col.default);
      }
    }
    // Ensure at least one column is visible
    const anyVisible = Array.from(visibility.values()).some(Boolean);
    if (!anyVisible && ordered.length > 0) {
      visibility.set(ordered[0].key, true);
    }

    return { columns: ordered, visibility, widths };
  }
}
